import { AutoAwesome, EmojiEvents, Star } from '@mui/icons-material';
import { alpha, Box, Chip, Paper, Popover, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MAX_SUPER_POWERS } from '@sharedInterfaces/globalEnums';
import type { ISuperPowersData } from '@sharedInterfaces/IEmployeeTalkState';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, type DropResult } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import type { ITemplateComponentProps } from '../TemplateEditor/types';
import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';
import { useEmployeeTalk } from './ETTEmployeeTalkProvider';

const ETTSuperPowersComponent: React.FC<ITemplateComponentProps> = (props) =>
{
    const theme = useTheme();
    const { secureState, selectSuperPower, deselectSuperPower, isReadOnlyForCurrentUser } = useEmployeeTalk();

    const [selectedSuperPowers, setSelectedSuperPowers] = useState<string[]>([]);
    const [popover, setPopover] = useState<{ open: boolean; message: string; anchorEl: HTMLElement | null }>({
        open: false,
        message: '',
        anchorEl: null,
    });

    const employee = useSelector((state: AppState) => state.employee);
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);

    const maxLevel = levelDefinitions.length > 0 ? Math.max(...levelDefinitions.map((l) => l.level)) : 4;
    const secondHighestLevel = Math.max(1, maxLevel - 1);

    // Use skills from talkState (always up-to-date via WS) with fallback to employee Redux state
    const talkStateSkills = React.useMemo(() =>
    {
        const skillComponent = Object.values(secureState?.components || {}).find(
            (c) => c.componentType === 'skillExtraction',
        );
        const skills = (skillComponent?.value as { skills?: Array<{ title: string; level: number; selected?: boolean }> })?.skills;
        return skills?.filter((s) => s.selected && s.level > 0) || [];
    }, [secureState]);

    // Merge: Employee skills (full profile) + talkState skills (latest onboarding changes)
    // TalkState skills override employee skills with same title (they have the latest level)
    const effectiveSkills: Array<{ title: string; level: number }> = React.useMemo(() =>
    {
        const employeeSkills = (employee.skills || []).map(s => ({ title: s.title, level: s.level }));
        if (talkStateSkills.length === 0) return employeeSkills;

        // Start with employee skills, then override/add with talkState skills
        const merged = new Map<string, { title: string; level: number }>();
        for (const s of employeeSkills) merged.set(s.title, s);
        for (const s of talkStateSkills) merged.set(s.title, { title: s.title, level: s.level });
        return Array.from(merged.values());
    }, [employee.skills, talkStateSkills]);

    const highestLevelSkills = effectiveSkills
        .filter((skill) => skill.level === maxLevel)
        .map((skill) => skill.title)
        .sort();

    const secondHighestLevelSkills = effectiveSkills
        .filter((skill) => skill.level === secondHighestLevel)
        .map((skill) => skill.title)
        .sort();

    useEffect(() =>
    {
        const componentData = secureState?.components[props.component.id]?.value as ISuperPowersData | undefined;
        if (componentData?.selectedSuperPowers)
        {
            setSelectedSuperPowers(componentData.selectedSuperPowers);
        }
    }, [secureState, props.component.id]);

    const handleToggleSuperPower = async (skill: string, event: React.MouseEvent<HTMLDivElement>) =>
    {
        const isSelected = selectedSuperPowers.includes(skill);

        if (isSelected)
        {
            try
            {
                await deselectSuperPower(skill);
                setSelectedSuperPowers((prev) => prev.filter((s) => s !== skill));
            } catch (error)
            {
                setPopover({
                    open: true,
                    message: error instanceof Error ? error.message : 'Fehler beim Entfernen der SuperPower',
                    anchorEl: event.currentTarget,
                });
            }
        } else
        {
            if (selectedSuperPowers.length >= MAX_SUPER_POWERS)
            {
                setPopover({
                    open: true,
                    message: `Du kannst maximal ${MAX_SUPER_POWERS} SuperPowers auswählen.`,
                    anchorEl: event.currentTarget,
                });
                return;
            }

            try
            {
                await selectSuperPower(skill);
                setSelectedSuperPowers((prev) => [...prev, skill]);
            } catch (error)
            {
                setPopover({
                    open: true,
                    message: error instanceof Error ? error.message : 'Fehler beim Hinzufügen der SuperPower',
                    anchorEl: event.currentTarget,
                });
            }
        }
    };

    const handleDragEnd = async (result: DropResult) =>
    {
        if (!result.destination || isReadOnlyForCurrentUser) return;

        const items = Array.from(selectedSuperPowers);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setSelectedSuperPowers(items);

        // Update component data with new order
        const componentEntry = Object.entries(secureState?.components || {}).find(
            ([, comp]) => comp.componentType === 'superPowers',
        );
        if (componentEntry)
        {
            const [componentId] = componentEntry;
            try
            {
                await secureState?.components[componentId]?.value;
                // Note: Order is stored in component state, not in Employee entity
            } catch (error)
            {
                console.error('Failed to update SuperPower order:', error);
                // Rollback on error
                const existingData = secureState?.components[componentId]?.value as ISuperPowersData | undefined;
                setSelectedSuperPowers(existingData?.selectedSuperPowers || []);
            }
        }
    };

    const renderViewContent = () => (
        <Box
            sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                borderRadius: 3,
                p: 4,
                textAlign: 'center',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                <EmojiEvents sx={{ fontSize: 48, color: 'primary.main' }} />
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    Deine SuperPowers
                </Typography>
            </Box>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
                Wähle bis zu 6 deiner Skills aus, die dich besonders auszeichnen!
            </Typography>

            {selectedSuperPowers.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                        ✨ Ausgewählt ({selectedSuperPowers.length}/6) - Sortiere per Drag & Drop
                    </Typography>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="superpowers" direction="vertical">
                            {(provided) => (
                                <Stack spacing={1} {...provided.droppableProps} ref={provided.innerRef}>
                                    {selectedSuperPowers.map((skill, index) => (
                                        <Draggable
                                            key={skill}
                                            draggableId={skill}
                                            index={index}
                                            isDragDisabled={isReadOnlyForCurrentUser}
                                        >
                                            {(provided, snapshot) => (
                                                <Paper
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    elevation={snapshot.isDragging ? 4 : 0}
                                                    sx={{
                                                        p: 2,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        background: snapshot.isDragging
                                                            ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                                                            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                                        color: 'white',
                                                        border: `2px solid ${theme.palette.primary.main}`,
                                                        borderRadius: 2,
                                                        cursor: isReadOnlyForCurrentUser ? 'default' : 'grab',
                                                        transition: 'background 0.2s ease',
                                                        ...(snapshot.isDragging && {
                                                            left: '0 !important',
                                                            right: '0 !important',
                                                        }),
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            minWidth: 32,
                                                            height: 32,
                                                            borderRadius: '50%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            bgcolor: alpha('#fff', 0.3),
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {index + 1}
                                                    </Box>
                                                    <Star />
                                                    <Typography sx={{ flex: 1, fontWeight: 500 }}>{skill}</Typography>
                                                    {!isReadOnlyForCurrentUser && (
                                                        <Chip
                                                            label="Entfernen"
                                                            size="small"
                                                            onClick={(e) =>
                                                            {
                                                                e.stopPropagation();
                                                                handleToggleSuperPower(skill, e);
                                                            }}
                                                            sx={{
                                                                bgcolor: alpha('#fff', 0.2),
                                                                color: 'white',
                                                                '&:hover': { bgcolor: alpha('#fff', 0.3) },
                                                            }}
                                                        />
                                                    )}
                                                </Paper>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </Stack>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Box>
            )}

            {highestLevelSkills.filter((skill) => !selectedSuperPowers.includes(skill)).length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}>
                        🏆 Level {maxLevel} Skills
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap justifyContent="center">
                        {highestLevelSkills
                            .filter((skill) => !selectedSuperPowers.includes(skill))
                            .map((skill) => (
                                <Chip
                                    key={skill}
                                    label={skill}
                                    icon={<AutoAwesome />}
                                    onClick={
                                        isReadOnlyForCurrentUser ? undefined : (e) => handleToggleSuperPower(skill, e)
                                    }
                                    sx={{
                                        fontSize: '1.1rem',
                                        py: 3,
                                        px: 2,
                                        background: alpha(theme.palette.primary.main, 0.15),
                                        color: 'text.primary',
                                        border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                                        cursor: isReadOnlyForCurrentUser ? 'default' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px) scale(1.05)',
                                            boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                                            background: alpha(theme.palette.primary.main, 0.25),
                                        },
                                    }}
                                />
                            ))}
                    </Stack>
                </Box>
            )}

            {secondHighestLevelSkills.filter((skill) => !selectedSuperPowers.includes(skill)).length > 0 && (
                <Box>
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, mb: 2, color: theme.palette.secondary.main }}
                    >
                        ⭐ Level {secondHighestLevel} Skills
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap justifyContent="center">
                        {secondHighestLevelSkills
                            .filter((skill) => !selectedSuperPowers.includes(skill))
                            .map((skill) => (
                                <Chip
                                    key={skill}
                                    label={skill}
                                    icon={<AutoAwesome />}
                                    onClick={
                                        isReadOnlyForCurrentUser ? undefined : (e) => handleToggleSuperPower(skill, e)
                                    }
                                    sx={{
                                        fontSize: '1.1rem',
                                        py: 3,
                                        px: 2,
                                        background: alpha(theme.palette.secondary.main, 0.1),
                                        color: 'text.primary',
                                        border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                                        cursor: isReadOnlyForCurrentUser ? 'default' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px) scale(1.05)',
                                            boxShadow: `0 8px 16px ${alpha(theme.palette.secondary.main, 0.3)}`,
                                            background: alpha(theme.palette.secondary.main, 0.2),
                                        },
                                    }}
                                />
                            ))}
                    </Stack>
                </Box>
            )}

            {selectedSuperPowers.length > 0 && (
                <Box
                    sx={{
                        mt: 4,
                        p: 2,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    }}
                >
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                        ✨ {selectedSuperPowers.length} SuperPower{selectedSuperPowers.length > 1 ? 's' : ''}{' '}
                        ausgewählt!
                    </Typography>
                </Box>
            )}

            <Popover
                open={popover.open}
                anchorEl={popover.anchorEl}
                onClose={() => setPopover({ ...popover, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: 'error.main',
                            color: 'white',
                            p: 2,
                            borderRadius: 2,
                            boxShadow: 3,
                        },
                    },
                }}
            >
                <Typography sx={{ fontWeight: 500 }}>{popover.message}</Typography>
            </Popover>
        </Box>
    );

    return <ETTBaseTemplateComponent {...props} renderViewContent={renderViewContent} />;
};

export default ETTSuperPowersComponent;
